import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Droplets } from "lucide-react";

import { ApiError } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { Modal } from "../../components/Modal";
import { Button, Input, Select, Textarea, useDescarteProtegido, useHayCambios } from "../../components/ui";
import {
  TIPO_INTERRUPCION_LABELS,
  crearInterrupcion,
  type InterrupcionAguaCreateInput,
  type NivelAfectacion,
  type TipoInterrupcionAgua,
} from "./api";

const TIPOS: TipoInterrupcionAgua[] = [
  "Programada",
  "FuerzaMayor",
  "CasoFortuito",
];

const MATERIALES = ["PVC", "HDPE", "PPR", "Otro"];

const FORM_ID = "crear-interrupcion-form";

export function CrearInterrupcionModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const toast = useToast();
  const [tipo, setTipo] = useState<TipoInterrupcionAgua>("CasoFortuito");
  const [nivel, setNivel] = useState<NivelAfectacion>("Parcial");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaTermino, setFechaTermino] = useState("");
  const [motivo, setMotivo] = useState("");
  const [ubicacion, setUbicacion] = useState("");
  const [usuarios, setUsuarios] = useState("0");
  const [diametro, setDiametro] = useState("");
  const [material, setMaterial] = useState("");
  const [longitud, setLongitud] = useState("");
  const [avisoUsuarios, setAvisoUsuarios] = useState("");
  const [avisoSiss, setAvisoSiss] = useState("");
  const [observaciones, setObservaciones] = useState("");

  const mut = useMutation({
    mutationFn: (input: InterrupcionAguaCreateInput) => crearInterrupcion(input),
    onSuccess: () => onCreated(),
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo registrar";
      toast.error(msg);
    },
  });

  function onSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (motivo.trim().length < 3) {
      toast.error("Motivo debe tener al menos 3 caracteres");
      return;
    }
    if (!fechaInicio) {
      toast.error("Fecha de inicio es obligatoria");
      return;
    }
    mut.mutate({
      tipo,
      nivel_afectacion: nivel,
      fecha_inicio: new Date(fechaInicio).toISOString(),
      fecha_termino: fechaTermino ? new Date(fechaTermino).toISOString() : null,
      motivo: motivo.trim(),
      ubicacion_falla: ubicacion.trim() || null,
      usuarios_afectados: Number(usuarios) || 0,
      diametro_matriz_mm: diametro ? Number(diametro) : null,
      material_matriz: material || null,
      longitud_tramo_m: longitud || null,
      fecha_aviso_usuarios: avisoUsuarios || null,
      fecha_aviso_siss: avisoSiss || null,
      observaciones: observaciones.trim() || null,
    });
  }

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios(
    [
      tipo, nivel, fechaInicio, fechaTermino, motivo, ubicacion, usuarios, diametro,
      material, longitud, avisoUsuarios, avisoSiss, observaciones,
    ],
  );
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    mut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Nueva interrupción de agua potable"
      icon={Droplets}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button type="submit" form={FORM_ID} disabled={mut.isPending}>
            {mut.isPending ? "Guardando…" : "Registrar"}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={onSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Tipo"
            required
            value={tipo}
            onChange={(e) => setTipo(e.target.value as TipoInterrupcionAgua)}
          >
            {TIPOS.map((t) => (
              <option key={t} value={t}>
                {TIPO_INTERRUPCION_LABELS[t]}
              </option>
            ))}
          </Select>
          <Select
            label="Nivel"
            required
            value={nivel}
            onChange={(e) => setNivel(e.target.value as NivelAfectacion)}
          >
            <option value="Parcial">Parcial</option>
            <option value="Total">Total</option>
          </Select>
          <Input
            label="Inicio"
            required
            type="datetime-local"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
          />
          <Input
            label="Término"
            type="datetime-local"
            value={fechaTermino}
            onChange={(e) => setFechaTermino(e.target.value)}
          />
        </div>
        <Textarea
          label="Motivo"
          required
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          rows={2}
          placeholder="Rotura de matriz, mantención programada, falla bomba…"
        />
        <Input
          label="Ubicación de la falla"
          value={ubicacion}
          onChange={(e) => setUbicacion(e.target.value)}
        />
        <div className="grid grid-cols-3 gap-3">
          <Input
            label="Usuarios afectados"
            type="number"
            value={usuarios}
            onChange={(e) => setUsuarios(e.target.value)}
            min={0}
          />
          <Input
            label="Diámetro matriz (mm)"
            type="number"
            value={diametro}
            onChange={(e) => setDiametro(e.target.value)}
          />
          <Select
            label="Material matriz"
            value={material}
            onChange={(e) => setMaterial(e.target.value)}
          >
            <option value="">—</option>
            {MATERIALES.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </Select>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Input
            label="Longitud tramo (m)"
            type="number"
            step="0.01"
            value={longitud}
            onChange={(e) => setLongitud(e.target.value)}
          />
          <Input
            label="Aviso a usuarios (≥3d antes)"
            type="date"
            value={avisoUsuarios}
            onChange={(e) => setAvisoUsuarios(e.target.value)}
          />
          <Input
            label="Aviso a SISS (≤24h)"
            type="date"
            value={avisoSiss}
            onChange={(e) => setAvisoSiss(e.target.value)}
          />
        </div>
        <Textarea
          label="Observaciones"
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
          rows={2}
        />
      </form>
      {dialogoDescarte}
    </Modal>
  );
}
